/**
 * WFC — Welcome sign-up behaviour (Workstream 2, designed by Jojo)
 *
 * Handles both placements (grid card + product-page line) via the shared
 * [data-wfc-signup-card] root:
 *   1. Hide for 2 weeks once someone signs up or closes it.
 *   2. Show a short note instead of the plain pitch when a bigger automatic
 *      discount already applies to the cart:
 *        - Spend & Save tier reached  -> spendSave note (takes priority)
 *        - A box of crackers in cart  -> snapPack note (free snap pack)
 *   3. Submit the email through Shopify's own newsletter (customer) form.
 *      Omnisend syncs Shopify email subscribers, so the existing Omnisend
 *      Welcome automation sends the free book bag code.
 *
 * Install changes (Oct 2026):
 *   - Replaced the unconfirmed Omnisend endpoint with Shopify's customer form.
 *   - Thresholds now follow Unicorn's per-country settings (checked 6 Oct 2026).
 *   - Cracker-box check uses the boxed-cracker SKU prefix "R-SO-6-".
 *   - localStorage wrapped in try/catch so private browsing can't break it.
 *
 * Fix (8 Oct 2026): Shopify's spam protection rejects background (fetch)
 * submissions with "Missing CAPTCHA token" (HTTP 400). The form now submits
 * the normal way, so Shopify can run its robot check. Shopify then returns
 * the visitor to the same page with ?customer_posted=true, where we hide the
 * card for 2 weeks and show a small "check your inbox" message.
 */

(function () {
  'use strict';

  var WFC_SIGNUP_CONFIG = {
    dismissDays: 14, // 2 weeks, matches the welcome code's validity window
    storageKey: 'wfc_signup_dismissed_until',
    pendingKey: 'wfc_signup_pending',
    // Unicorn Spend & Save thresholds (checked against Unicorn, 6 Oct 2026).
    // Country thresholds override currency thresholds — same as in Unicorn.
    spendSaveCountryThresholds: { NZ: 150, AU: 150, CA: 150, US: 100, GB: 75 },
    spendSaveCurrencyThresholds: { NZD: 150 },
    // Boxed reusable crackers (6-pack) all have SKUs starting with this.
    crackerBoxSkuPattern: /^R-SO-6-/i,
    // Switched off 3 Nov 2026: the free book bag code now combines with
    // Spend & Save, the free snap pack and Black Friday, so there is no
    // "keep your code for next time" message to show.
    conditionalNotes: {},
    successHeading: "You're in. Check your inbox.",
    // {email} is replaced with the address they typed (kept for this tab only).
    successMessage: "Your code is on its way to {email}, with the gingerbread guide. Add a book bag, plus any other item, then enter the code at checkout. It's good until 24 December.",
    successMessageNoEmail: "Your code is on its way, with the gingerbread guide. Add a book bag, plus any other item, then enter the code at checkout. It's good until 24 December."
  };

  function isDismissed() {
    try {
      var until = window.localStorage.getItem(WFC_SIGNUP_CONFIG.storageKey);
      if (!until) return false;
      return Date.now() < parseInt(until, 10);
    } catch (e) {
      return false;
    }
  }

  function setDismissed() {
    try {
      var until = Date.now() + WFC_SIGNUP_CONFIG.dismissDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(WFC_SIGNUP_CONFIG.storageKey, String(until));
    } catch (e) { /* storage unavailable — ignore */ }
  }

  function setPending(email) {
    try { window.sessionStorage.setItem(WFC_SIGNUP_CONFIG.pendingKey, email || '1'); } catch (e) {}
  }

  function takePending() {
    try {
      var v = window.sessionStorage.getItem(WFC_SIGNUP_CONFIG.pendingKey);
      window.sessionStorage.removeItem(WFC_SIGNUP_CONFIG.pendingKey);
      return v; // the email address, '1', or null
    } catch (e) {
      return false;
    }
  }

  function hideCard(card) {
    card.setAttribute('hidden', 'hidden');
    card.style.display = 'none';
  }

  /* Shopify sends the visitor back with ?customer_posted=true after a
     successful newsletter sign-up. */
  function handleReturnFromSignup() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var posted = params.get('customer_posted') === 'true';
    var pending = takePending();
    if (!posted || !pending) return;

    setDismissed();
    document.querySelectorAll('[data-wfc-signup-card]').forEach(hideCard);

    var toast = document.createElement('div');
    toast.className = 'wfc-signup-toast';
    toast.setAttribute('role', 'status');
    var heading = document.createElement('strong');
    heading.style.display = 'block';
    heading.textContent = WFC_SIGNUP_CONFIG.successHeading;
    var body = document.createElement('span');
    body.textContent = (pending && pending !== '1')
      ? WFC_SIGNUP_CONFIG.successMessage.replace('{email}', pending)
      : WFC_SIGNUP_CONFIG.successMessageNoEmail;
    toast.appendChild(heading);
    toast.appendChild(body);
    document.body.appendChild(toast);
    setTimeout(function () { toast.classList.add('wfc-signup-toast--hide'); }, 10000);
    setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 10600);
  }

  function getThreshold() {
    var shopify = window.Shopify || {};
    var country = shopify.country;
    var currency = shopify.currency && shopify.currency.active;
    if (country && WFC_SIGNUP_CONFIG.spendSaveCountryThresholds[country]) {
      return WFC_SIGNUP_CONFIG.spendSaveCountryThresholds[country];
    }
    if (currency && WFC_SIGNUP_CONFIG.spendSaveCurrencyThresholds[currency]) {
      return WFC_SIGNUP_CONFIG.spendSaveCurrencyThresholds[currency];
    }
    return null;
  }

  /**
   * Looks at /cart.js and returns 'spendSave', 'snapPack' or null.
   * Messaging only — Shopify's own discount rules decide what applies at checkout.
   */
  function checkCartForBetterDiscount() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var threshold = getThreshold();
        var subtotal = (cart.items_subtotal_price || cart.total_price || 0) / 100; // cents
        if (threshold && subtotal >= threshold) return 'spendSave';

        var hasCrackerBox = (cart.items || []).some(function (item) {
          return WFC_SIGNUP_CONFIG.crackerBoxSkuPattern.test(item.sku || '');
        });
        if (hasCrackerBox) return 'snapPack';
        return null;
      })
      .catch(function () { return null; });
  }

  function applyConditionalNote(card) {
    var noteEl = card.querySelector('[data-wfc-conditional-note]');
    if (!noteEl) return;
    checkCartForBetterDiscount().then(function (match) {
      if (match && WFC_SIGNUP_CONFIG.conditionalNotes[match]) {
        noteEl.textContent = WFC_SIGNUP_CONFIG.conditionalNotes[match];
        noteEl.hidden = false;
      }
    });
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function handleSubmit(card, form) {
    form.addEventListener('submit', function (e) {
      var emailInput = form.querySelector('input[type="email"]');
      var email = emailInput ? emailInput.value.trim() : '';
      var errorEl = form.querySelector('[data-wfc-signup-error]');

      if (!isValidEmail(email)) {
        e.preventDefault();
        if (errorEl) errorEl.hidden = false;
        if (emailInput) emailInput.focus();
        return;
      }
      if (errorEl) errorEl.hidden = true;

      // Let the browser submit the form normally so Shopify's spam
      // protection (robot check) can run. Shopify brings the visitor back
      // to this page with ?customer_posted=true when it worked.
      setPending(email);
      var submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        setTimeout(function () { submitBtn.disabled = true; }, 0);
      }
    });
  }

  function initCard(card) {
    if (isDismissed()) {
      hideCard(card);
      return;
    }

    var dismissBtn = card.querySelector('[data-wfc-signup-dismiss]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        setDismissed();
        hideCard(card);
      });
    }

    var form = card.querySelector('form.wfc-signup-form');
    if (form) handleSubmit(card, form);

    // Product-page version: click the link to reveal the email box.
    if (card.dataset.wfcVariant === 'pdp') {
      var openTrigger = card.querySelector('[data-wfc-signup-pdp-open]');
      if (openTrigger && form) {
        openTrigger.addEventListener('click', function () {
          form.classList.remove('wfc-is-collapsed');
          var line = openTrigger.closest('[data-wfc-signup-pdp-line]');
          if (line) line.hidden = true;
          var input = form.querySelector('input[type="email"]');
          if (input) input.focus();
        });
      }
    }

    applyConditionalNote(card);
  }

  /* ---------- Placement ----------
     The markup lives in <template> tags output by layout/theme.liquid.
     Collection pages: the card goes into the product grid after the 4th
     product (moved up from 12th on 3 Nov 2026 so phone shoppers see it).
     Product pages: the line goes directly under the Add to Cart buttons of
     the main product (not quick-buy or featured-product sections). */

  var GRID_POSITION = 4;
  var GRID_MIN_PRODUCTS = 4;

  function cloneTemplate(id) {
    var tpl = document.getElementById(id);
    if (!tpl || !tpl.content) return null;
    return tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : null;
  }

  function placeGridCard() {
    if (isDismissed()) return;
    var list = document.querySelector('.shopify-section--main-collection product-list');
    if (!list || list.querySelector('[data-wfc-signup-card]')) return;
    var cards = list.querySelectorAll(':scope > product-card');
    if (!cards.length) cards = list.querySelectorAll(':scope > .product-card');
    if (cards.length < GRID_MIN_PRODUCTS) return;
    var anchor = cards[Math.min(GRID_POSITION, cards.length) - 1];
    var card = cloneTemplate('wfc-signup-grid-template');
    if (anchor && card) anchor.insertAdjacentElement('afterend', card);
  }

  function placePdpLine() {
    if (isDismissed()) return;
    var buyButtons = Array.prototype.find.call(
      document.querySelectorAll('[data-block-type="buy-buttons"]'),
      function (el) {
        var section = el.closest('.shopify-section');
        return section && /__main/.test(section.id);
      }
    );
    if (!buyButtons || buyButtons.querySelector('[data-wfc-signup-card]')) return;
    var line = cloneTemplate('wfc-signup-pdp-template');
    if (line) buyButtons.appendChild(line);
  }

  function init() {
    placeGridCard();
    placePdpLine();
    document.querySelectorAll('[data-wfc-signup-card]').forEach(function (card) {
      if (card.dataset.wfcReady) return;
      card.dataset.wfcReady = '1';
      initCard(card);
    });
  }

  function start() {
    handleReturnFromSignup();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Collection filters/pagination re-render the grid without a page reload.
  document.addEventListener('shopify:section:load', init);
  var initTimer = null;
  new MutationObserver(function () {
    clearTimeout(initTimer);
    initTimer = setTimeout(init, 200);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
